import React, { useRef, useState } from 'react';
import axios from 'axios';
import { BelegAktion, BelegKarte as BelegKarteDaten } from '../types';
import {
  aktionLabel, primaereAktion, nachAktion, nummerAnzeige, statusKlasse, statusLabel,
  formatBetrag, herkunftText, auftragZeile,
} from '../lib/belegKarte';
import {
  kannZuAuftragNavigieren, navigiereZuAuftrag, kannZuPartnerNavigieren, navigiereZuPartner,
} from '../lib/navigation';
import BelegAnsichtModal from './BelegAnsichtModal';
import './BelegKarte.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  karte: BelegKarteDaten;
  /** Meldet den neuen Kartenstand (nach Senden/Buchen/…) an die Nachrichtenliste */
  onChange?: (k: BelegKarteDaten) => void;
  /** Aktionen, die in diesem Kontext nichts bringen (z. B. „Zum Kunden" in der Kundenakte selbst). */
  ausblenden?: BelegAktion[];
}

/** Aktionen, die einen Server-Aufruf auslösen (Rest: Ansicht, Formular, Navigation). */
type ServerAktion = 'senden' | 'vorlegen' | 'buchen' | 'genehmigen' | 'verwerfen' | 'ablehnen';

interface Formular {
  zahlungsziel: string;
  leistungsdatum: string;
  kopftext: string;
  fusstext: string;
}

const TYP_LABEL: Record<BelegKarteDaten['typ'], string> = {
  rechnung: 'Rechnung',
  lieferschein: 'Lieferschein',
  zahlung: 'Zahlung',
  freigabe: 'Freigabe',
};

function freigabeId(k: BelegKarteDaten): number {
  return k.freigabe_id ?? k.id;
}

/** Endpunkt + Body je Server-Aktion (alle ohne Trailing-Slash). */
function anfrage(k: BelegKarteDaten, a: ServerAktion): { url: string; body?: unknown } {
  switch (a) {
    case 'senden':
      return { url: `${API_URL}/api/email/ausgangsrechnung/${k.id}`, body: {} };
    case 'vorlegen':
      return { url: `${API_URL}/api/dokumente/ausgangsrechnungen/${k.id}/vorlegen` };
    case 'buchen':
      return k.typ === 'lieferschein'
        ? { url: `${API_URL}/api/lieferscheine/${k.id}/versenden` }
        : { url: `${API_URL}/api/freigaben/${freigabeId(k)}/genehmigen` };
    case 'genehmigen':
      return { url: `${API_URL}/api/freigaben/${freigabeId(k)}/genehmigen` };
    case 'verwerfen':
      return { url: `${API_URL}/api/freigaben/${freigabeId(k)}/ablehnen`, body: { grund: 'Im Chat verworfen' } };
    case 'ablehnen':
      return { url: `${API_URL}/api/freigaben/${freigabeId(k)}/ablehnen`, body: { grund: 'Im Chat abgelehnt' } };
  }
}

const ERFOLG: Partial<Record<ServerAktion, string>> = {
  vorlegen: 'Der Buchhaltung zur Prüfung vorgelegt.',
};

function fehlerText(err: unknown): string {
  const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
  return typeof detail === 'string' && detail
    ? detail
    : 'Die Aktion ist fehlgeschlagen. Bitte erneut versuchen.';
}

/** Belegkarte im Chat: der Agent legt Belege nur als Entwurf an, jede Aktion
 *  nach außen (Senden, Buchen, Genehmigen) löst erst der Klick hier aus. */
const BelegKarte: React.FC<Props> = ({ karte, onChange, ausblenden }) => {
  const [busy, setBusy] = useState<BelegAktion | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [ansichtOffen, setAnsichtOffen] = useState(false);
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false);
  const leer: Formular = {
    zahlungsziel: karte.zahlungsziel_tage != null ? String(karte.zahlungsziel_tage) : '',
    leistungsdatum: '', kopftext: '', fusstext: '',
  };
  const [formular, setFormular] = useState<Formular>(leer);
  // Letzter gespeicherter Stand: nur geänderte Felder gehen per PUT raus
  const gespeichert = useRef<Formular>(leer);

  const primaer = primaereAktion(karte);
  const zumAuftragMoeglich = !!karte.auftrag && kannZuAuftragNavigieren();
  const sichtbar = karte.aktionen.filter(a => {
    if (ausblenden?.includes(a)) return false;
    if (a === 'zum_auftrag') return zumAuftragMoeglich;
    if (a === 'zum_kunden') return !!karte.kunde?.partner_id && kannZuPartnerNavigieren();
    if (a === 'ansehen' || a === 'ansehen_rechnung') return !!karte.ansicht_url;
    return true;
  });

  const ausfuehren = async (a: ServerAktion) => {
    if (busy) return;
    setBusy(a);
    setFehler(null);
    setHinweis(null);
    try {
      const { url, body } = anfrage(karte, a);
      const res = await axios.post(url, body);
      const antwort = res.data && typeof res.data === 'object' ? res.data as Record<string, unknown> : {};
      setBearbeitenOffen(false);
      setHinweis(ERFOLG[a] ?? null);
      onChange?.(nachAktion(karte, a, antwort));
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setBusy(null);
    }
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const alt = gespeichert.current;
    const body: Record<string, unknown> = {};
    if (formular.zahlungsziel !== alt.zahlungsziel && formular.zahlungsziel.trim() !== '') {
      const tage = Number(formular.zahlungsziel);
      if (!Number.isInteger(tage) || tage < 0) {
        setFehler('Zahlungsziel bitte als ganze Zahl von Tagen angeben.');
        return;
      }
      body.zahlungsziel_tage = tage;
    }
    if (formular.leistungsdatum !== alt.leistungsdatum && formular.leistungsdatum) {
      body.leistungsdatum = formular.leistungsdatum;
    }
    if (formular.kopftext !== alt.kopftext) body.kopftext = formular.kopftext;
    if (formular.fusstext !== alt.fusstext) body.fusstext = formular.fusstext;
    if (Object.keys(body).length === 0) {
      setBearbeitenOffen(false);
      return;
    }
    setBusy('bearbeiten');
    setFehler(null);
    setHinweis(null);
    try {
      const res = await axios.put(`${API_URL}/api/dokumente/ausgangsrechnungen/${karte.id}`, body);
      const r = (res.data ?? {}) as Record<string, unknown>;
      const neu: Formular = {
        zahlungsziel: r.zahlungsziel_tage != null ? String(r.zahlungsziel_tage) : formular.zahlungsziel,
        leistungsdatum: typeof r.leistungsdatum === 'string' ? r.leistungsdatum : formular.leistungsdatum,
        kopftext: typeof r.kopftext === 'string' ? r.kopftext : formular.kopftext,
        fusstext: typeof r.fusstext === 'string' ? r.fusstext : formular.fusstext,
      };
      gespeichert.current = neu;
      setFormular(neu);
      setBearbeitenOffen(false);
      setHinweis('Änderungen gespeichert.');
      const ziel = typeof r.zahlungsziel_tage === 'number' ? r.zahlungsziel_tage : karte.zahlungsziel_tage;
      onChange?.({ ...karte, zahlungsziel_tage: ziel });
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setBusy(null);
    }
  };

  const klick = (a: BelegAktion) => {
    switch (a) {
      case 'ansehen':
      case 'ansehen_rechnung':
        setAnsichtOffen(true);
        return;
      case 'bearbeiten':
        setFehler(null);
        setBearbeitenOffen(v => !v);
        return;
      case 'zum_auftrag':
        if (karte.auftrag) navigiereZuAuftrag(karte.auftrag.auftrag_id);
        return;
      case 'zum_kunden':
        if (karte.kunde?.partner_id) navigiereZuPartner(karte.kunde.partner_id);
        return;
      default:
        ausfuehren(a);
    }
  };

  const knopfKlasse = (a: BelegAktion) => {
    if (a === primaer) return 'k-btn k-btn--primary k-btn--sm';
    if (a === 'verwerfen' || a === 'ablehnen') return 'k-btn k-btn--danger k-btn--sm';
    return 'k-btn k-btn--ghost k-btn--sm';
  };

  const auftrag = auftragZeile(karte);
  const herkunft = herkunftText(karte);
  // Primäraktion zuerst, Reihenfolge der übrigen wie geliefert
  const knoepfe = primaer && sichtbar.includes(primaer)
    ? [primaer, ...sichtbar.filter(a => a !== primaer)]
    : sichtbar;

  return (
    <div className="k-belegkarte">
      <div className="k-belegkarte__head">
        <span className="k-belegkarte__art">{TYP_LABEL[karte.typ]}</span>
        <span className={`k-belegkarte__badge k-belegkarte__badge--${statusKlasse(karte.status)}`}>
          {statusLabel(karte.status, karte.typ)}
        </span>
      </div>
      <div className="k-belegkarte__titel">{karte.titel || nummerAnzeige(karte)}</div>

      <dl className="k-belegkarte__zeilen">
        {karte.kunde?.name && (<><dt>Kunde</dt><dd>{karte.kunde.name}</dd></>)}
        {auftrag && (<><dt>Auftrag</dt><dd>{auftrag}</dd></>)}
        {karte.betraege && (karte.betraege.netto === karte.betraege.brutto ? (
          <><dt>Betrag</dt><dd className="k-belegkarte__zahl">{formatBetrag(karte.betraege.brutto)}</dd></>
        ) : (
          <>
            <dt>Netto</dt><dd className="k-belegkarte__zahl">{formatBetrag(karte.betraege.netto)}</dd>
            <dt>Brutto</dt><dd className="k-belegkarte__zahl">{formatBetrag(karte.betraege.brutto)}</dd>
          </>
        ))}
        {karte.zahlungsziel_tage != null && (
          <><dt>Zahlungsziel</dt><dd>{karte.zahlungsziel_tage} Tage</dd></>
        )}
        {karte.empfaenger && (<><dt>Empfänger</dt><dd>{karte.empfaenger}</dd></>)}
      </dl>

      {bearbeitenOffen && (
        <form className="k-belegkarte__formular" onSubmit={speichern}>
          <label className="k-belegkarte__label">
            Zahlungsziel (Tage)
            <input
              className="k-belegkarte__feld" type="number" min={0} step={1}
              value={formular.zahlungsziel}
              onChange={e => setFormular(f => ({ ...f, zahlungsziel: e.target.value }))}
              disabled={!!busy}
            />
          </label>
          <label className="k-belegkarte__label">
            Leistungsdatum
            <input
              className="k-belegkarte__feld" type="date"
              value={formular.leistungsdatum}
              onChange={e => setFormular(f => ({ ...f, leistungsdatum: e.target.value }))}
              disabled={!!busy}
            />
          </label>
          <label className="k-belegkarte__label">
            Kopftext
            <textarea
              className="k-belegkarte__feld k-belegkarte__text"
              value={formular.kopftext}
              onChange={e => setFormular(f => ({ ...f, kopftext: e.target.value }))}
              disabled={!!busy}
            />
          </label>
          <label className="k-belegkarte__label">
            Fußtext
            <textarea
              className="k-belegkarte__feld k-belegkarte__text"
              value={formular.fusstext}
              onChange={e => setFormular(f => ({ ...f, fusstext: e.target.value }))}
              disabled={!!busy}
            />
          </label>
          <p className="k-belegkarte__notiz">Leere Felder bleiben unverändert.</p>
          <div className="k-belegkarte__aktionen">
            <button type="submit" className="k-btn k-btn--primary k-btn--sm" disabled={!!busy}>
              {busy === 'bearbeiten' ? 'Speichert …' : 'Speichern'}
            </button>
            <button
              type="button" className="k-btn k-btn--ghost k-btn--sm"
              onClick={() => { setFormular(gespeichert.current); setBearbeitenOffen(false); }}
              disabled={!!busy}
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {fehler && <p className="k-belegkarte__fehler" role="alert">{fehler}</p>}
      {hinweis && <p className="k-belegkarte__hinweis" role="status">{hinweis}</p>}

      {knoepfe.length > 0 && (
        <div className="k-belegkarte__aktionen">
          {knoepfe.map(a => (
            <button
              key={a}
              type="button"
              className={knopfKlasse(a)}
              onClick={() => klick(a)}
              disabled={!!busy}
              aria-expanded={a === 'bearbeiten' ? bearbeitenOffen : undefined}
            >
              {busy === a ? 'Bitte warten …' : aktionLabel(a, karte.typ)}
            </button>
          ))}
        </div>
      )}

      <div className="k-belegkarte__fuss">{herkunft}</div>

      {ansichtOffen && karte.ansicht_url && (
        <BelegAnsichtModal
          url={`${API_URL}${karte.ansicht_url}`}
          title={karte.titel}
          kontext={{}}
          onClose={() => setAnsichtOffen(false)}
        />
      )}
    </div>
  );
};

export default BelegKarte;
